import { Avatar, Tooltip, makeStyles } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { LocalGasStation } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import { memo, useMemo } from 'react';

import { getBrandIcon } from '../modules/data';
import { engineFuelDetails, getLabel } from '../modules/labels';

type PackageDetailBrandFuelInfoProps = {
  brands?: { [key: string]: number };
  engineFuel?: { [key: string]: number };
};

const useStyles = makeStyles((theme) => ({
  body: {
    padding: theme.spacing(0, 2, 1),
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
  },
  brandInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    overflow: 'hidden',
  },
  brandAvatar: {
    width: 20,
    height: 20,
    backgroundColor: grey[100],
    '& img': {
      objectFit: 'contain',
      padding: 3,
    },
  },
  brandCount: {
    fontSize: 12,
    fontWeight: 600,
    color: grey[700],
  },
  brandName: {
    fontSize: 12,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailChip: {
    fontSize: 11,
    color: grey[700],
    backgroundColor: grey[100],
    borderRadius: 4,
    padding: '2px 6px',
    alignContent: 'center',
  },
  fuelSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    borderTop: `1px solid ${grey[200]}`,
    paddingTop: 8,
    alignItems: 'center',
  },
}));

const PackageDetailBrandFuelInfo = ({ brands, engineFuel }: PackageDetailBrandFuelInfoProps) => {
  const classes = useStyles();
  const brandEntries = useMemo(() => (brands ? Object.entries(brands).sort((a, b) => b[1] - a[1]) : []), [brands]);

  if (!brands || brandEntries.length === 0) return null;

  return (
    <div>
      <div className={classes.body}>
        {brandEntries.map(([brand, count]) => (
          <div key={brand} className={classes.brandRow}>
            <div className={classes.brandInfo}>
              <Tooltip title={brand} arrow>
                <Avatar
                  className={classes.brandAvatar}
                  src={getBrandIcon(brand)}
                  alt={brand}
                  imgProps={{
                    onError: (e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    },
                  }}
                >
                  {brand.charAt(0)}
                </Avatar>
              </Tooltip>
              <span className={classes.brandCount}>{count}</span>
              <span className={classes.brandName}>{brand}</span>
            </div>
          </div>
        ))}
      </div>
      {!isEmpty(engineFuel) && (
        <div className={classes.fuelSection} style={{ padding: '8px 16px 16px' }}>
          <LocalGasStation style={{ fontSize: 20, color: 'black', flexShrink: 0 }} />
          <span className={classes.brandCount}>{Object.keys(engineFuel!).length}</span>
          {Object.entries(engineFuel!)
            .sort((a, b) => b[1] - a[1])
            .map(([fuel, count]) => (
              <span key={fuel} className={classes.detailChip}>
                {getLabel(engineFuelDetails, fuel)} ({count})
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

export default memo(PackageDetailBrandFuelInfo);
